import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useJsonLd, buildRoutesListJsonLd } from "../lib/seo";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { veniceDisplayLabel } from "../components/VeniceSubPicker";
import { 
  MapPin, 
  Clock, 
  ArrowRight, 
  Search,
  Mountain,
  Ship,
  Building2,
  Waves,
  Grape,
  Globe,
  Plane,
  Map,
  Home as HomeIcon,
  User,
  LogOut,
  Menu,
  X,
  ChevronRight
} from "lucide-react";

const CATEGORY_ICONS = {
  "Dolomites": Mountain,
  "Cruise Terminals": Ship,
  "Cities": Building2,
  "Lakes": Waves,
  "Tuscany": Grape,
  "International": Globe,
  "Airports": Plane,
  "Tours": Map,
  "Local": MapPin,
  "Riviera": Waves
};

const CATEGORY_COLORS = {
  "Dolomites": "from-blue-500/20 to-cyan-500/20",
  "Cruise Terminals": "from-purple-500/20 to-pink-500/20",
  "Cities": "from-amber-500/20 to-orange-500/20",
  "Lakes": "from-teal-500/20 to-emerald-500/20",
  "Tuscany": "from-rose-500/20 to-red-500/20",
  "International": "from-indigo-500/20 to-violet-500/20",
  "Airports": "from-slate-500/20 to-gray-500/20",
  "Tours": "from-lime-500/20 to-green-500/20",
  "Local": "from-sky-500/20 to-blue-500/20",
  "Riviera": "from-cyan-500/20 to-teal-500/20"
};

export default function RoutesPage() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchRoutes = useCallback(async () => {
    try {
      const routesRes = await axios.get(`${API}/routes`);
      setRoutes(routesRes.data);
      // Derive categories from routes data
      const cats = {};
      routesRes.data.forEach(route => {
        const cat = route.category;
        if (!cats[cat]) cats[cat] = [];
        cats[cat].push(route);
      });
      setCategories(cats);
    } catch (error) {
      console.error("Failed to load routes", error);
      toast.error("Could not load routes. Please retry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // SEO: BreadcrumbList + ItemList of every route (for Google rich results + AI crawlers).
  const routesListJsonLd = useMemo(
    () => buildRoutesListJsonLd({ routes }),
    [routes]
  );
  useJsonLd(routesListJsonLd);

  const filteredRoutes = routes.filter(route => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === "" || 
      route.origin.toLowerCase().includes(term) ||
      route.destination.toLowerCase().includes(term) ||
      veniceDisplayLabel(route.origin).toLowerCase().includes(term) ||
      veniceDisplayLabel(route.destination).toLowerCase().includes(term);
    const matchesCategory = selectedCategory === "all" || route.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categoryList = Object.keys(categories);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-2xl font-bold logo-text" data-testid="logo">TN</Link>
              <span className="hidden sm:block text-white/60 text-xs tracking-[0.3em] uppercase">TaxiNearMe</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/routes" className="nav-link text-[#C5A059]">Routes</Link>
              <Link to="/faq" className="nav-link" data-testid="nav-faq">FAQ</Link>
              {user ? (
                <div className="flex items-center gap-4">
                  <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-white/70 hover:text-black hover:bg-[#C5A059]">
                    <User className="w-4 h-4 mr-2" /> Dashboard
                  </Button>
                  <Button variant="ghost" onClick={logout} className="text-white/70 hover:text-red-400">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button onClick={login} className="btn-outline-gold">Sign In</Button>
              )}
            </div>

            <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0A0A] border-t border-white/5 py-4">
            <div className="flex flex-col gap-4 px-6">
              <Link to="/" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/routes" className="nav-link py-2 text-[#C5A059]" onClick={() => setMobileMenuOpen(false)}>Routes</Link>
              <Link to="/faq" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-faq">FAQ</Link>
              {user ? (
                <>
                  <Button variant="ghost" onClick={() => navigate("/dashboard")} className="justify-start text-white/70">Dashboard</Button>
                  <Button variant="ghost" onClick={logout} className="justify-start text-red-400">Sign Out</Button>
                </>
              ) : (
                <Button onClick={login} className="btn-gold">Sign In</Button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            All <span className="text-[#C5A059]">Routes</span>
          </h1>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            Explore our complete collection of premium transfer routes from Venice to destinations across Italy and beyond.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <Input
              placeholder="Search destinations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-dark pl-12 h-12"
              data-testid="search-routes"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === "all" ? "default" : "ghost"}
              onClick={() => setSelectedCategory("all")}
              className={selectedCategory === "all" ? "bg-[#C5A059] text-black" : "text-white/60"}
              size="sm"
            >
              All
            </Button>
            {categoryList.map(cat => {
              const Icon = CATEGORY_ICONS[cat] || MapPin;
              return (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "ghost"}
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? "bg-[#C5A059] text-black" : "text-white/60"}
                  size="sm"
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {cat}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-[#121212] border border-white/5 rounded-sm p-4 text-center">
            <div className="text-3xl font-bold text-[#C5A059]">{routes.length}</div>
            <div className="text-white/50 text-sm">Total Routes</div>
          </div>
          <div className="bg-[#121212] border border-white/5 rounded-sm p-4 text-center">
            <div className="text-3xl font-bold text-white">{categoryList.length}</div>
            <div className="text-white/50 text-sm">Categories</div>
          </div>
          <div className="bg-[#121212] border border-white/5 rounded-sm p-4 text-center">
            <div className="text-3xl font-bold text-white">24/7</div>
            <div className="text-white/50 text-sm">Service</div>
          </div>
          <div className="bg-[#121212] border border-white/5 rounded-sm p-4 text-center">
            <div className="text-3xl font-bold text-white">2-7</div>
            <div className="text-white/50 text-sm">Passengers</div>
          </div>
        </div>
      </section>

      {/* Routes Grid */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : filteredRoutes.length === 0 ? (
          <div className="text-center py-20">
            <MapPin className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">No routes found matching your criteria</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="routes-grid">
            {filteredRoutes.map((route) => {
              const Icon = CATEGORY_ICONS[route.category] || MapPin;
              const colorClass = CATEGORY_COLORS[route.category] || "from-gray-500/20 to-slate-500/20";
              
              return (
                <Link
                  to={`/route/${route.slug}`}
                  key={route.route_id}
                  className="group bg-[#121212] border border-white/5 rounded-sm overflow-hidden hover:border-[#C5A059]/30 transition-all duration-300"
                  data-testid={`route-card-${route.slug}`}
                >
                  {/* Category Header */}
                  <div className={`bg-gradient-to-r ${colorClass} px-4 py-2 flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-white/70" />
                      <span className="text-xs text-white/70 uppercase tracking-wide">{route.category}</span>
                    </div>
                    {route.seo_priority === "Top" && (
                      <span className="text-[10px] bg-[#C5A059]/20 text-[#C5A059] px-2 py-0.5 rounded uppercase">Popular</span>
                    )}
                  </div>

                  {/* Route Info */}
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <MapPin className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white">{veniceDisplayLabel(route.origin)}</span>
                        <ArrowRight className="w-4 h-4 text-white/30" />
                        <span className="text-white font-semibold">{veniceDisplayLabel(route.destination)}</span>
                      </div>
                    </div>

                    {/* Meta Info - No Price */}
                    <div className="flex items-center gap-4 text-sm text-white/40">
                      {route.duration_hours && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          ~{route.duration_hours}h
                        </span>
                      )}
                      {route.distance_km && (
                        <span>{route.distance_km} km</span>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                      <span className="text-sm text-white/50">View details & book</span>
                      <ChevronRight className="w-5 h-5 text-[#C5A059] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-2xl font-bold logo-text">TaxiNearMe</div>
            <div className="text-white/40 text-sm">
              © 2026 TaxiNearMe. Premium transfers from Venice.
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4 flex-wrap justify-center text-white/30 text-sm border-t border-white/5 pt-4" data-testid="footer-legal-links">
            <Link to="/privacy" className="hover:text-[#C5A059] transition-colors">Privacy Policy</Link>
            <span className="text-white/15">•</span>
            <Link to="/legal/cookies" className="hover:text-[#C5A059] transition-colors">Cookie Policy</Link>
            <span className="text-white/15">•</span>
            <Link to="/legal/terms" className="hover:text-[#C5A059] transition-colors">Terms</Link>
            <span className="text-white/15">•</span>
            <button type="button" onClick={() => window.dispatchEvent(new Event("tnm-open-cookie-preferences"))} className="hover:text-[#C5A059] transition-colors">
              Cookie preferences
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
